/**
 * Phase 4D — Tutor Force-Change-Password Page.
 *
 * After admin approves a tutor, the Welcome email contains a temporary
 * password. On first login the backend `/auth/me` returns
 * `force_password_change: true` and Tutor Dashboard redirects here.
 * Tutor cannot reach the dashboard until they change the password.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Lock, Eye, EyeOff, Check, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const TutorForceChangePasswordPage = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [cf, setCf] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await ax.get('/api/auth/me');
        if (!live) return;
        if ((r.data?.role || '').toLowerCase() !== 'tutor') {
          navigate('/portal/login');
          return;
        }
        if (!r.data?.force_password_change) {
          navigate('/tutor/dashboard');
          return;
        }
        setMe(r.data);
      } catch {
        navigate('/tutor/login');
      }
    })();
    return () => { live = false; };
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (nw.length < 8) { setErr('New password must be at least 8 characters.'); return; }
    if (nw !== cf) { setErr('Passwords do not match.'); return; }
    if (nw === cur) { setErr('New password must be different from the temporary one.'); return; }
    setBusy(true);
    try {
      await ax.post('/api/auth/change-password', { current_password: cur, new_password: nw });
      toast.success('Password updated. Welcome to the Tutor Portal.');
      navigate('/tutor/dashboard');
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Could not change password.');
    } finally { setBusy(false); }
  };

  if (!me) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#D3D86F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4" data-testid="tutor-force-change-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[4px] text-[#D3D86F] font-bold">Winhouse Tutors</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mt-2">Set your portal password</h1>
          <p className="text-xs sm:text-sm text-gray-400 mt-2">Your account was just provisioned by Winhouse. Please replace the temporary password before continuing to the Tutor Dashboard.</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl p-6 space-y-4">
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <ShieldAlert className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-[11px] text-amber-800">
              Signed in as <strong>{me.email}</strong>. You will be redirected to the Tutor Dashboard after setting a new password.
            </div>
          </div>

          <Field label="Temporary password (from welcome email)" type={show ? 'text' : 'password'} v={cur} on={setCur} testid="force-pw-current" />
          <Field label="New password" type={show ? 'text' : 'password'} v={nw} on={setNw} testid="force-pw-new" hint="Min 8 characters" />
          <Field label="Confirm new password" type={show ? 'text' : 'password'} v={cf} on={setCf} testid="force-pw-confirm" />

          <button type="button" onClick={() => setShow((s) => !s)} className="text-[11px] text-gray-500 flex items-center gap-1 hover:text-gray-700" data-testid="force-pw-toggle">
            {show ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />} {show ? 'Hide' : 'Show'} passwords
          </button>

          {err && <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="force-pw-error">{err}</div>}

          <button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 disabled:opacity-40" data-testid="force-pw-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Set password & continue
          </button>

          <div className="text-[10px] text-center text-gray-400 pt-2">
            <Lock className="h-3 w-3 inline mr-1" /> Your password is hashed before it touches our database.
          </div>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, type, v, on, testid, hint }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1">{label}</label>
    <input type={type} required value={v} onChange={(e) => on(e.target.value)}
      className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-200" data-testid={testid} />
    {hint && <div className="text-[10px] text-gray-400 mt-1">{hint}</div>}
  </div>
);

export default TutorForceChangePasswordPage;
